/**
 * The game's round defaults in the admin's round panel (Hide and Seek and Hide
 * and Seek Teams): the settings every new round starts with. Saving them
 * changes the unstarted rounds that follow them; Custom rounds keep theirs.
 */

import { useState, type ComponentProps } from 'react';
import { useSocketContext, formatDuration } from '@swtorbit/shared-react-hooks';
import { Modal } from '@swtorbit/ui/react';
import {
  GameType,
  SocketEvent,
  hideSeekRoundDefaultsOf,
  isHideSeekSettings,
  resolveHideSeekRoundDefaults,
  type Game,
  type HnsUpdateRoundDefaultsRequest,
  type HnsUpdateRoundDefaultsResponse,
} from '@swtorbit/shared-socket-events';
import { HnsRoundSettingsForm } from './HnsRoundSettingsForm';

function roundsLabel(count: number): string {
  return `${count} round${count === 1 ? '' : 's'}`;
}

export function HnsRoundDefaultsPanel({ game }: { game: Game }) {
  const { socket } = useSocketContext();
  const isTeams = game.gameType === GameType.HIDE_AND_SEEK_TEAMS;
  const defaults = resolveHideSeekRoundDefaults(isTeams ? GameType.HIDE_AND_SEEK_TEAMS : GameType.HIDE_AND_SEEK, game.hnsRoundDefaults);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const unstarted = game.rounds.filter((round) => round.status === 'setup' && isHideSeekSettings(round.settings));
  const following = unstarted.filter((round) => isHideSeekSettings(round.settings) && round.settings.usesGameDefaults).length;

  const save: ComponentProps<typeof HnsRoundSettingsForm>['onSubmit'] = (settings, _players, events) => {
    if (!socket) return;
    setSaving(true);
    setError(null);
    socket.emit(
      SocketEvent.HNS_UPDATE_ROUND_DEFAULTS,
      { roomCode: game.roomCode, defaults: hideSeekRoundDefaultsOf(settings, events) } satisfies HnsUpdateRoundDefaultsRequest,
      (res: HnsUpdateRoundDefaultsResponse) => {
        setSaving(false);
        if (!res?.success) {
          setError(res?.error ?? 'Failed to save the round defaults');
          return;
        }
        setEditing(false);
        const updated = res.updatedRounds ?? 0;
        const custom = res.customRounds ?? 0;
        setSaved(`Saved. ${roundsLabel(updated)} changed to the new defaults${custom > 0 ? `; ${roundsLabel(custom)} kept ${custom === 1 ? 'its' : 'their'} own settings` : ''}.`);
      },
    );
  };

  return (
    <div className="card p-sm mb-sm">
      <div className="d-flex justify-content-between align-items-center gap-xs">
        <h4 className="m-0">⚙️ Round defaults</h4>
        <button
          type="button"
          className="btn btn--outline btn--slate"
          onClick={() => { setSaved(null); setError(null); setEditing(true); }}
          disabled={saving}
        >
          Edit
        </button>
      </div>

      <p className="fs-sm mt-xs mb-0">
        Hiding {formatDuration(defaults.hidingDuration)} · Round {defaults.roundDuration === 0 ? 'unlimited' : formatDuration(defaults.roundDuration)}
        {' '}· Marco Polo ±{defaults.marcoPoloRadius} m
        {defaults.roundEvents.length > 0 && ` · ${defaults.roundEvents.length} timeline event${defaults.roundEvents.length === 1 ? '' : 's'}`}
      </p>
      <p className="fs-sm text-slate mt-xs mb-0">
        New rounds start with these.
        {unstarted.length > 0 && ` ${following} of ${unstarted.length} unstarted ${unstarted.length === 1 ? 'round follows' : 'rounds follow'} them; the others are Custom.`}
      </p>
      {saved && <p className="fs-sm text-green mt-xs mb-0">{saved}</p>}

      <Modal open={editing} onClose={() => setEditing(false)} className="round-creation-modal" title="Round Defaults">
        {editing && (
          <HnsRoundSettingsForm
            mode={isTeams ? 'hnst' : 'hns'}
            players={[]}
            roundDefaults={defaults}
            editingDefaults
            onSubmit={save}
            onCancel={() => setEditing(false)}
          />
        )}
        {error && <div className="field__error">{error}</div>}
      </Modal>
    </div>
  );
}
